import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RequestChecker } from '~lib/request-checker';
import { environment } from '../../../environments/environment';

export class OrderPollWebResult {
  zipStatus: ChargeStatus;
  gifting?: GiftcardDelivery;
  cashback?: string;
}

export type GiftcardDelivery = {
  targetAddress: string;
  deliveryStatus: OrderDeliveryStatus;
  channel: OrderDeliveryChannel;
  gifting?: GiftCardGiftingRequest;
  metadata?: any;
  receiptId?: string;
};
export type GiftCardGiftingRequest = {
  recipientEmail: string;
  recipientEmailVerification: string;
  recipientName: string;
  message: string;
  deliveryDate?: string;
  messageImageURL?: string;
};

export enum OrderDeliveryStatus {
  ATTEMPTED = 'ATTEMPTED',
  DELIVERED = 'DELIVERED',
  BOUNCED = 'BOUNCED',
  FAILED = 'FAILED',
}

export enum OrderDeliveryChannel {
  EMAIL = 'EMAIL',
  ON_THE_SPOT = 'ON_THE_SPOT',
}

export enum ChargeStatus {
  PENDING = 'PENDING',
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
  PENDING_NO_RETRY = 'PENDING_NO_RETRY',
  PENDING_FULFILLMENT = 'PENDING_FULFILLMENT',
}

export interface ChargeResponse {
  orderId: string;
  zipStatus: ChargeStatus;
}

type OrderPollInput = {
  simulatePendingTil?: number;
  pollForSecs?: number;
  disableOnTheSpotDelivery?: boolean;
  orderId: string;
};

@Injectable()
export class CheckoutService {
  public responseComplete: any;

  constructor(private ngHttp: HttpClient) {
    this.responseComplete = (resp: ChargeResponse) =>
      resp && resp.zipStatus && resp.zipStatus !== ChargeStatus.PENDING;
  }

  checkiOSVersion() {
    var agent = window.navigator.userAgent.toLowerCase(),
      start = agent.indexOf('/'),
      end = agent.indexOf(' ', start);

    if (/iphone|ipod|ipad/.test(agent) && start > -1) {
      let iosVerion = agent.substr(start, end - start).replace('/', '');
      if (iosVerion >= '1.1.5') {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  checkandroidVersion() {
    var agent = window.navigator.userAgent.toLowerCase(),
      start = agent.indexOf('/'),
      end = agent.indexOf(' ', start);

    if (/android/.test(agent) && start > -1) {
      let androidVerion = agent.substr(start, end - start).replace('/', '');
      if (androidVerion >= '1.1.2') {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  async init({
    price,
    cardName,
    cardId,
    imageUri,
    name,
    email,
    vendor,
    discount,
    comebackUri,
    gifting,
    merchantUniqueId,
    merchantId,
  }) {
    const checkoutParams = {
      price,
      cardName,
      cardId,
      imageUri,
      vendor,
      discount,
      name,
      email,
      redirectUri: comebackUri,
      gifting,
      merchantUniqueId,
      merchantId,
    };

    const urlWithParams = `${environment.apiHost}/checkout`;
    return this.ngHttp.post<any>(urlWithParams, checkoutParams).toPromise();
  }

  async getCheckout(checkoutId: string, merchantUniqueId: string, merchantId: string) {
    return this.ngHttp
      .post<any>(`${environment.apiHost}/checkout/find`, {
        checkoutId,
        merchantUniqueId,
        merchantId,
      })
      .toPromise();
  }

  async charge(chargeInfo): Promise<OrderPollWebResult & { orderId: string }> {
    const response: ChargeResponse = await this.ngHttp
      .post<ChargeResponse>(`${environment.apiHost}/charge`, chargeInfo)
      .toPromise();

    if (this.responseComplete(response)) {
      return response;
    }
    // TODO orderId can be used to access giftcards with no auth (via zip.web.gifted proxy url) so fraud risk (this URL is not expossed to the frontend so should be ok)
    return { orderId: response.orderId, ...(await this.orderPollWeb(response)) };
  }

  async orderPollWeb({
    pollForSecs = 5,
    disableOnTheSpotDelivery,
    ...respOrder
  }: OrderPollInput): Promise<OrderPollWebResult> {
    const intervalSecs = 2;
    const requestChecker = new RequestChecker<OrderPollWebResult>({
      request$: this.ngHttp.post<OrderPollWebResult>(
        `${environment.apiHost}/order/web-poll`,
        respOrder,
      ),
      interval: intervalSecs * 1000,
      timeout: pollForSecs * 1000,
      callback: this.responseComplete,
      failedAttempts: pollForSecs / intervalSecs,
    });

    requestChecker.start();

    const result = await requestChecker.promise;

    if (disableOnTheSpotDelivery && result.zipStatus === ChargeStatus.SUCCESS) {
      result.zipStatus = ChargeStatus.PENDING;
    }
    return result;
  }
}
